import { Autocomplete } from '@dtx-company/design-system/src/components/Autocomplete/Autocomplete'
import { Box } from '@dtx-company/design-system/src/components/Box/Box'
import { Category } from '@dtx-company/flow-codegen/src/code/generated.types'
import { FC, useEffect, useRef } from 'react'
import { PrintStoreTemplatesFilterProps } from './types'
import { Typography } from '@dtx-company/design-system/src/components/Typography/Typography'
import {
  getTemplateFiltersKey,
  hydrateFromUrl
} from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreSlice'
import { styled } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import { usePrintStoreTemplatesFilters } from './hooks'
import Chip from '@mui/material/Chip'
import MuiCheckbox from '@mui/material/Checkbox'

const StyledListItem = styled('li')({
  paddingTop: 0.75,
  paddingBottom: 0.75,
  paddingLeft: '0 !important'
})

export const PrintStoreTemplatesFilter: FC<PrintStoreTemplatesFilterProps> = ({
  sectionName,
  clearTemplates
}) => {
  const { categories, selectedCategories, filterLabel, onChangeHandler, sectionNameToQueryParam } =
    usePrintStoreTemplatesFilters({
      sectionName,
      clearTemplates
    })

  const router = useFlowRouter()
  const dispatch = useDispatch()
  const queryParam = sectionNameToQueryParam[sectionName]
  const hasSetCategories = useRef(false)

  useEffect(() => {
    if (!hasSetCategories.current) {
      if (queryParam) {
        const queryList = router.getString(queryParam)
        const queryArrays = queryList?.split(',')
        const queryCategories: Category[] = []

        if (queryArrays) {
          queryArrays.forEach(query => {
            const foundCategory = categories.find(
              category => category.categoryName?.toLowerCase() === query?.toLowerCase()
            )
            const foundExistsInCategories = queryCategories.some(
              category => category.categoryId === foundCategory?.categoryId
            )
            // to handle different cases of the same category name
            if (foundCategory && !foundExistsInCategories) queryCategories.push(foundCategory)
          })
          if (queryCategories.length) {
            const sectionKey = getTemplateFiltersKey(sectionName)
            dispatch(
              hydrateFromUrl({
                [sectionKey]: queryCategories
              })
            )
            hasSetCategories.current = true
          }
        }
      }
    }
  }, [categories, selectedCategories, queryParam, router, dispatch, hasSetCategories, sectionName])

  return (
    <Autocomplete<Category, true>
      id={`print-store-templates-filters-${sectionName}`}
      label={filterLabel}
      size="small"
      value={selectedCategories}
      multiple
      disableCloseOnSelect
      isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
      onChange={onChangeHandler}
      renderTags={(value, tagProps) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {value.slice(0, 2).map((option, index) => (
            <Chip
              {...tagProps}
              key={`${sectionName}_${option.categoryId}`}
              label={
                <Typography noWrap>
                  {option.categoryName}
                  {index < value.length - 1 && value.length > 1 ? ', ' : ''}
                </Typography>
              }
              sx={{
                maxWidth: theme => (value.length > 1 ? theme.spacing(30) : '100%'),
                height: 'unset',
                backgroundColor: 'unset',
                '&:hover': {
                  background: 'unset'
                },
                px: 0,
                span: { px: 0 }
              }}
            />
          ))}
          {value.length > 2 && <Typography>+ {value.length - 2} more</Typography>}
        </Box>
      )}
      limitTags={2}
      options={categories}
      getOptionLabel={option => option.categoryName}
      renderOption={(props, option, { selected }) => (
        <StyledListItem {...props} key={`${sectionName}_${option.categoryId}`}>
          <MuiCheckbox checked={selected} />
          {option.categoryName}
        </StyledListItem>
      )}
      sx={theme => ({
        width: { xs: '100%', md: 'unset' },
        minWidth: { md: theme.spacing(55) },
        maxWidth: { md: '50%' }
      })}
    />
  )
}
